import { Autocomplete, Box, Chip, TextField, createFilterOptions } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import { useDirection } from '../../context/DirectionContext';
import { translate as tr } from '../../i18n/translations';
import { SharedParameter } from '../../types/api';
import NewParameterDialog from './NewParameterDialog';

// Sentinel option rendered last: opens NewParameterDialog instead of selecting.
const NEW_ID = '__new_parameter__';
const NEW: SharedParameter = { id: NEW_ID, name: '', description: null, maxGrade: 0 };
const filter = createFilterOptions<SharedParameter>({ stringify: (o) => o.name });

type Common = {
  options: SharedParameter[];
  /** Stored free-text name of an old, unlinked value: prefills the dialog and shows the "Not linked" chip. */
  legacyName?: string;
  label?: string;
};
type Single = Common & { multiple?: false; value: string | null; onChange: (p: SharedParameter | null) => void };
type Multiple = Common & { multiple: true; value: string[]; onChange: (ps: SharedParameter[]) => void };
export type ParameterPickerProps = Single | Multiple;

const ParameterPicker: React.FC<ParameterPickerProps> = (props) => {
  const { options, legacyName, label } = props;
  const { lang } = useDirection();
  const qc = useQueryClient();
  const [input, setInput] = useState('');
  // What the admin typed (not a selected label): prefills the new-parameter dialog.
  const [typed, setTyped] = useState('');
  const [dialogName, setDialogName] = useState<string | null>(null);
  // Params created here, shown before the ['parameters'] refetch lands.
  const [created, setCreated] = useState<SharedParameter[]>([]);

  // Stable references matter: MUI resets the input text whenever `value` or getOptionLabel changes identity.
  const all = useMemo(
    () => [...options, ...created.filter((c) => !options.some((o) => o.id === c.id))],
    [options, created],
  );
  // Unresolved IDs in multiple mode (bank loading/failed, or another unit's id on a shared row):
  // keep them as placeholder chips so they aren't silently dropped; never show the raw id.
  const unresolvedPlaceholders = useMemo(() => {
    if (!props.multiple) return [];
    const known = new Set(all.map((o) => o.id));
    return props.value
      .filter((id) => id && !known.has(id))
      .map((id): SharedParameter => ({ id, name: tr('param.unavailable', lang), description: null, maxGrade: 0 }));
  }, [props.multiple, props.value, all, lang]);
  const allWithPlaceholders = useMemo(
    () => (unresolvedPlaceholders.length ? [...all, ...unresolvedPlaceholders] : all),
    [all, unresolvedPlaceholders],
  );
  const ids = props.multiple ? props.value.join('\u0000') : props.value ?? '';
  const selected: SharedParameter[] = useMemo(
    () => (ids ? ids.split('\u0000') : []).map((id) => allWithPlaceholders.find((o) => o.id === id)).filter((p): p is SharedParameter => !!p),
    [ids, allWithPlaceholders],
  );
  const onInputChange = useCallback((_: unknown, v: string, reason: string) => {
    setInput(v);
    setTyped(reason === 'input' ? v : '');
  }, []);
  const getOptionLabel = useCallback(
    (o: SharedParameter) => (o.id === NEW_ID ? tr('param.newInBank', lang) : o.name),
    [lang],
  );
  const empty = props.multiple ? props.value.length === 0 : !props.value;

  const emit = (next: SharedParameter[]) => {
    if (props.multiple) props.onChange(next);
    else props.onChange(next[0] ?? null);
  };

  const handleChange = (_: unknown, v: SharedParameter | SharedParameter[] | null) => {
    const list = Array.isArray(v) ? v : v ? [v] : [];
    if (list.some((o) => o.id === NEW_ID)) {
      setDialogName(typed.trim() || legacyName || '');
      return;
    }
    emit(list);
  };

  const handleCreated = (p: SharedParameter) => {
    setDialogName(null);
    setCreated((c) => [...c, p]);
    qc.setQueryData<SharedParameter[]>(['parameters'], (old) => {
      if (!old) return [p];
      if (old.some((x) => x.id === p.id)) return old;
      return [...old, p];
    });
    qc.invalidateQueries({ queryKey: ['parameters'] });
    emit(props.multiple ? [...selected, p] : [p]);
  };

  const shared = {
    options: allWithPlaceholders,
    filterOptions: (opts: SharedParameter[], state: any) => [
      ...filter(opts.filter((o) => o.maxGrade > 0), state),
      NEW,
    ],
    getOptionLabel,
    isOptionEqualToValue: (a: SharedParameter, b: SharedParameter) => a.id === b.id,
    inputValue: input,
    onInputChange,
    renderOption: (liProps: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key }, o: SharedParameter) => {
      const { key, ...rest } = liProps;
      return (
        <li key={key} {...rest}>
          {o.id === NEW_ID ? (
            <Box component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {tr('param.newInBank', lang)}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
              <span>{o.name}</span>
              <Box component="span" dir="ltr" sx={{ color: 'text.secondary' }}>{`1–${o.maxGrade}`}</Box>
            </Box>
          )}
        </li>
      );
    },
    renderInput: (params: any) => (
      <TextField
        {...params}
        size="small"
        label={label ?? tr(props.multiple ? 'param.pickMany' : 'param.pick', lang)}
        placeholder={empty ? legacyName : undefined}
        // keep the label up so the old name (placeholder) is visible without focusing
        InputLabelProps={{ ...params.InputLabelProps, ...(empty && legacyName ? { shrink: true } : {}) }}
      />
    ),
    size: 'small' as const,
    fullWidth: true,
  };

  return (
    <Box>
      {props.multiple ? (
        <Autocomplete<SharedParameter, true> {...shared} multiple value={selected} onChange={handleChange} />
      ) : (
        <Autocomplete<SharedParameter, false> {...shared} value={selected[0] ?? null} onChange={handleChange} />
      )}
      {empty && legacyName && (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={tr('param.notLinked', lang)}
          sx={{ mt: 0.75, fontSize: '0.7rem' }}
        />
      )}
      <NewParameterDialog
        open={dialogName !== null}
        initialName={dialogName ?? ''}
        onClose={() => setDialogName(null)}
        onCreated={handleCreated}
      />
    </Box>
  );
};

export default ParameterPicker;
